import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchPublicAnuncios } from "@/lib/public-anuncios";
import { handleInvalidSession } from "@/lib/session-guard";

interface Interesse {
  id: string;
  item_id: string;
  item_nome: string;
  teto_coins: number | null;
  teto_reais: number | null;
  teto_kk: number | null;
  tiers: number[];
  servidores: string[];
  tiers_tetos: Record<string, { teto_coins: number | null; teto_reais: number | null; teto_kk: number | null }>;
}

export interface InteressesSummary {
  total: number;
  matches: Array<{ interesse: Interesse; anuncios: any[] }>;
  matchedItemNames: string[];
  allItemNames: string[];
  // Todos os anúncios de venda (de outros usuários) para itens cadastrados como
  // interesse, independente de tier/servidor/teto. Usado para promover esses
  // anúncios na home, alinhando com a contagem da aba do cliente.
  itemAnuncios: any[];
  // Quantidade de itens diferentes que têm anúncios de venda ativos (ignora compra e próprios)
  activeItemsCount: number;
}

const matchesInteresse = (interesse: Interesse, a: any): boolean => {
  // Only consider sale ads (ignore purchase ads)
  if (a.tipo !== "venda") return false;

  // Server filter
  if (interesse.servidores?.length && a.servidor && !interesse.servidores.includes(a.servidor)) {
    return false;
  }
  // Tier filter
  if (interesse.tiers?.length && a.tier != null && !interesse.tiers.includes(a.tier)) {
    return false;
  }

  // Get applicable price ceilings (per-tier override > global)
  const tierKey = a.tier != null ? String(a.tier) : null;
  const perTier = tierKey ? interesse.tiers_tetos?.[tierKey] : null;
  const tetoCoins = perTier?.teto_coins ?? interesse.teto_coins;
  const tetoReais = perTier?.teto_reais ?? interesse.teto_reais;
  const tetoKk = perTier?.teto_kk ?? interesse.teto_kk;

  // No ceilings = match anything that passed server/tier filters
  if (tetoCoins == null && tetoReais == null && tetoKk == null) return true;

  // Check if at least one matching ceiling is satisfied
  if (tetoCoins != null && a.preco_coins != null && Number(a.preco_coins) <= tetoCoins) return true;
  if (tetoReais != null && a.moeda === "reais" && a.preco != null && Number(a.preco) <= tetoReais) return true;
  if (tetoKk != null && a.preco_kk != null && Number(a.preco_kk) <= tetoKk) return true;

  return false;
};

export const useInteressesSummary = (
  contato: string | null | undefined,
  token: string | null | undefined,
  perfilId: string | null | undefined,
) => {
  return useQuery<InteressesSummary>({
    queryKey: ["interesses-summary", contato, token, perfilId],
    enabled: !!contato && !!token,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
    const empty: InteressesSummary = { total: 0, matches: [], matchedItemNames: [], allItemNames: [], itemAnuncios: [], activeItemsCount: 0 };
      let data: any = null;
      try {
        const res = await supabase.functions.invoke("client-interesses", {
          body: { contato, token, action: "list" },
        });
        if (res.error) {
          const msg = String((res.error as any)?.message || res.error || "");
          if (msg.includes("Token inválido") || msg.includes("Sessão inválida")) {
            handleInvalidSession();
          }
          return empty;
        }
        data = res.data;
      } catch {
        return empty;
      }
      if (data?.error) {
        const errStr = String(data.error);
        if (errStr === "Token inválido" || errStr === "Sessão inválida") {
          handleInvalidSession();
        }
        return empty;
      }
      const interesses: Interesse[] = data?.interesses || [];
      if (interesses.length === 0) {
        return empty;
      }

      const itemNames = [...new Set(interesses.map((i) => i.item_nome))];
      const anuncios = await fetchPublicAnuncios({ itemNames });
      const aprovados = anuncios.filter(
        (a: any) => a.status === "aprovado" && (!perfilId || a.anunciante_id !== perfilId)
      );

      const matches: InteressesSummary["matches"] = [];
      const matchedNames = new Set<string>();
      for (const interesse of interesses) {
        const list = aprovados.filter(
          (a: any) => a.item_nome === interesse.item_nome && matchesInteresse(interesse, a),
        );
        if (list.length > 0) {
          matches.push({ interesse, anuncios: list });
          matchedNames.add(interesse.item_nome);
        }
      }

      const itemAnuncios = aprovados.filter((a: any) => a.tipo === "venda");

      // Count unique items that have at least one active sale ad from other users
      const activeItemNames = new Set<string>(itemAnuncios.map((a: any) => a.item_nome?.toLowerCase()).filter(Boolean));

      return {
        total: interesses.length,
        matches,
        matchedItemNames: Array.from(matchedNames),
        allItemNames: itemNames,
        itemAnuncios,
        activeItemsCount: activeItemNames.size,
      };
    },
  });
};
